/**
 * Print a PDF without leaving the page: load it into a hidden frame and call the
 * frame's print(), which opens the browser's print dialog on the PDF itself.
 *
 * The report viewer (ReportPdfDialog) prints through this too. Without the
 * viewer it is a frame, a PDF blob and print(): the
 * Tray Ticket Visualizer already shows the ticket, so its Print goes straight to
 * the print dialog instead of opening a second preview and making the user find
 * Print again. The PDF is the same one the viewer prints - both are the stored
 * PRNX document exported by DevExpress on the API.
 */

/** Marks the frame, so a second print replaces the first instead of stacking frames. */
export const PRINT_FRAME_ATTR = 'data-report-print-frame';

/** Give up on the frame (and let the caller fall back to the viewer) after this long. A one-page PDF loads in well under a second. */
export const PRINT_FRAME_LOAD_TIMEOUT_MS = 10_000;

/** How long a finished frame stays in the DOM. Firefox's print() returns before the dialog closes. */
const FRAME_LIFETIME_MS = 10 * 60_000;

/**
 * True where printing a PDF from a hidden frame works. Phones and tablets don't:
 * iOS prints the page around the frame, Android shows nothing. Nor does a
 * browser that downloads PDFs instead of showing them (`pdfViewerEnabled`
 * false: Chrome's "Download PDFs" setting, headless browsers) - the frame
 * would save a file and never load. All of those keep the viewer, whose toolbar
 * handles printing there.
 */
export function canPrintPdfInPage(
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints ?? 0,
  pdfViewerEnabled: boolean | undefined = typeof navigator === 'undefined' ? undefined : navigator.pdfViewerEnabled,
): boolean {
  // Older browsers don't report it; only an explicit false rules the frame out.
  if (pdfViewerEnabled === false) return false;
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(userAgent)) return false;
  // iPadOS reports itself as a Mac; a Mac has no touch points.
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return false;
  return true;
}

function removeFrame(frame: HTMLIFrameElement) {
  const url = frame.dataset.blobUrl;
  frame.remove();
  if (url) URL.revokeObjectURL(url);
}

/**
 * Opens the print dialog for `pdf`. Resolves once print() has been called;
 * rejects when the frame cannot load or print it, so the caller can fall back.
 */
export function printPdfBlob(pdf: Blob, doc: Document = document): Promise<void> {
  doc.querySelectorAll<HTMLIFrameElement>(`iframe[${PRINT_FRAME_ATTR}]`).forEach(removeFrame);

  const blob = pdf.type === 'application/pdf' ? pdf : new Blob([pdf], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const frame = doc.createElement('iframe');
  frame.setAttribute(PRINT_FRAME_ATTR, '');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.title = 'Report print';
  frame.dataset.blobUrl = url;
  frame.dataset.state = 'loading';
  // Not display:none - Chrome does not start its PDF viewer in an undisplayed frame.
  Object.assign(frame.style, {
    position: 'absolute',
    width: '0',
    height: '0',
    border: '0',
    visibility: 'hidden',
  });

  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const fail = (reason: unknown) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      removeFrame(frame);
      reject(reason instanceof Error ? reason : new Error(String(reason)));
    };
    const timer = window.setTimeout(() => fail(new Error('The PDF did not load for printing')), PRINT_FRAME_LOAD_TIMEOUT_MS);

    frame.onload = () => {
      if (settled) return;
      try {
        const win = frame.contentWindow;
        if (!win) throw new Error('The print frame has no window');
        frame.dataset.state = 'printing';
        win.focus();
        win.print();
      } catch (e) {
        fail(e);
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      window.setTimeout(() => removeFrame(frame), FRAME_LIFETIME_MS);
      resolve();
    };
    frame.onerror = () => fail(new Error('The PDF could not be loaded for printing'));

    frame.src = url;
    doc.body.appendChild(frame);
  });
}
